import type {TranslationKey} from 'globalTranslations';
import {config} from '@modules/config';
import type {RatioedImageProps} from '@app/components/Image';

export enum TAB_INDEX {
	COMPUTER,
	MOBILE,
	WEARABLES,
	SOUND_SYSTEMS,
	VOICE,
	TV,
	CARS,
}

export const tabOptions: {
	tab: string;
	name: TranslationKey;
	path: string;
	id: TAB_INDEX;
	contentTabs: string[];
}[] = [
	{
		tab: 'computer',
		name: 'devicespage_title_desktopandlaptop_web',
		path: 'devices',
		id: TAB_INDEX.COMPUTER,
		contentTabs: ['pcmac'],
	},
	{
		tab: 'mobile',
		name: 'devicespage_title_mobileandtablets_web',
		path: 'devices/mobile',
		id: TAB_INDEX.MOBILE,
		contentTabs: ['apple', 'android'],
	},
	{
		tab: 'wearables',
		name: 'devicespage_title_wearables_web',
		path: 'devices/wearables',
		id: TAB_INDEX.WEARABLES,
		contentTabs: ['garmin', 'applewatch', 'androidwear'],
	},
	{
		tab: 'soundsystems',
		name: 'devicespage_title_speakers_web',
		path: 'devices/soundsystems',
		id: TAB_INDEX.SOUND_SYSTEMS,
		contentTabs: [
			'homepod',
			'onkyorubato',
			'pioneer',
			'sonos',
			'bose',
			'bang',
			'samsung',
			'amazonecho',
			'googlehome',
			'ultimate',
			'control4',
			'devialet',
			'yamaha',
			'harman',
			'lgaudio',
			'bluesound',
			'heos',
			'onkyo',
			'sonyaudio',
			'simaudio',
			'teufel',
		],
	},
	{
		tab: 'voice',
		name: 'devicespage_title_voiceassistants_web',
		path: 'devices/voice',
		id: TAB_INDEX.VOICE,
		contentTabs: ['amazonalexa', 'google', 'siri'],
	},
	{
		tab: 'tv',
		name: 'deviceslandingpage_title_television_standalone-web',
		path: 'devices/tv',
		id: TAB_INDEX.TV,
		contentTabs: [
			'appletv',
			'hisense',
			'androidtv',
			'samsungtv',
			'chromecast',
			'orange',
			'lg',
			'panasonic',
			'sony',
			'amazon',
			'portal',
		],
	},
	{
		tab: 'cars',
		name: 'Automobiles',
		path: 'devices/cars',
		id: TAB_INDEX.CARS,
		contentTabs: [
			'waze',
			'carplay',
			'androidauto',
			'mirrorlinkauto',
			'uconnect',
			'automotive',
		],
	},
];

export const pathUrl: Record<string, string> = {
	computer: 'devices',
	cars: 'cars',
	tv: 'tv',
	wearables: 'wearables',
	voice: 'voice',
	mobile: 'mobile',
	soundsystems: 'soundsystems',
	pcmac: 'computer',
	apple: 'mobile',
	android: 'mobile',
	garmin: 'wearables',
	applewatch: 'wearables',
	androidwear: 'wearables',
	homepod: 'soundsystems',
	onkyorubato: 'soundsystems',
	pioneer: 'soundsystems',
	sonos: 'soundsystems',
	bose: 'soundsystems',
	bang: 'soundsystems',
	samsung: 'soundsystems',
	amazonecho: 'soundsystems',
	googlehome: 'soundsystems',
	ultimate: 'soundsystems',
	control4: 'soundsystems',
	devialet: 'soundsystems',
	yamaha: 'soundsystems',
	harman: 'soundsystems',
	lgaudio: 'soundsystems',
	bluesound: 'soundsystems',
	heos: 'soundsystems',
	onkyo: 'soundsystems',
	sonyaudio: 'soundsystems',
	simaudio: 'soundsystems',
	teufel: 'soundsystems',
	amazonalexa: 'voice',
	google: 'voice',
	siri: 'voice',
	appletv: 'tv',
	hisense: 'tv',
	androidtv: 'tv',
	samsungtv: 'tv',
	chromecast: 'tv',
	orange: 'tv',
	lg: 'tv',
	panasonic: 'tv',
	sony: 'tv',
	amazon: 'tv',
	portal: 'tv',
	waze: 'cars',
	carplay: 'cars',
	androidauto: 'cars',
	mirrorlinkauto: 'cars',
	uconnect: 'cars',
	automotive: 'cars',
};

type TranslatedElement = {
	key: TranslationKey | TranslationKey[] | string;
	sprintf?: (string | number)[];
	count?: number;
};

type PictureConfig = Pick<
	Required<RatioedImageProps>,
	'alt' | 'src' | 'aspectRatio'
>;

export type DeviceContentConfig = {
	pathToDisplay?: string;
	displayCountry?: string[];
	image: PictureConfig;
	title: TranslatedElement;
	subTitle?: TranslatedElement;
	description: TranslatedElement & {hasLink?: boolean};
	subDescription?: TranslatedElement;
	withHifi?: boolean;
	withIosBadge?: boolean;
	withAndroidBadge?: boolean;
	brandImage?: PictureConfig;
	learnMoreLink?: string;
};

export interface DeviceConfig {
	seoTitle: TranslationKey;
	seoDescription: TranslationKey;
	configs: DeviceContentConfig[];
}

type DevicePage =
	| 'computer'
	| 'tv'
	| 'cars'
	| 'wearables'
	| 'voice'
	| 'mobile'
	| 'soundsystems';

export const devicePageConfig: Record<DevicePage | string, DeviceConfig> = {
	computer: {
		seoTitle: 'devicespage_text_seotitle_web',
		seoDescription: 'devicespage_text_seotitle_web',
		configs: [
			{
				pathToDisplay: 'pcmac',
				title: {key: 'Deezer dans votre navigateur'},
				description: {
					key: "Ouvrez votre navigateur, allez sur Deezer.com, appuyez sur play et montez le volume. Profiter de la musique que vous aimez sur votre ordinateur n'a jamais été aussi simple.",
				},
				image: {
					src: 'd872e1a88563e545e2ebd74d3f8c2214.png',
					alt: 'PC/Mac',
					aspectRatio: '720/420',
				},
			},
			{
				title: {key: 'Application pour ordinateur'},
				description: {
					key: 'devicespage_text_desktopapp_web',
					sprintf: ['HiFi'],
				},
				image: {
					aspectRatio: '1440/840',
					src: 'e2adab65c62df5fce5033f9ba72ab267.png',
					alt: 'Electron windows/mac',
				},
				learnMoreLink: '/explore/download',
			},
		],
	},
	mobile: {
		seoTitle: 'devicesmobiletabletspage_text_seotitle_web',
		seoDescription: 'devicesmobiletabletspage_text_seodescription_web',
		configs: [
			{
				pathToDisplay: 'android',
				title: {key: 'Android'},
				description: {key: 'devicespage_text_android_web'},
				image: {
					src: 'f2fc2148fbd5b14557189e934e18615a.png',
					aspectRatio: '720/420',
					alt: 'Avec Android',
				},
				subDescription: {
					key: 'devicespage_text_forandroidversionXorhigher_web',
					sprintf: [8],
				},
				withAndroidBadge: true,
			},
			{
				pathToDisplay: 'apple',
				image: {
					src: '0cb3e53fbf437a0bc60be37c5c4f3331.png',
					aspectRatio: '720/420',
					alt: 'Apple',
				},
				title: {key: 'Apple'},
				description: {key: 'devicespage_text_apple_web'},
				subDescription: {
					key: 'devicespage_text_foriosversionXorhigher_web',
					sprintf: [18.5],
				},
				withIosBadge: true,
			},
		],
	},
	cars: {
		seoTitle: 'devicescarspage_text_seotitle_web',
		seoDescription: 'devicescarspage_text_seodescription_web',
		configs: [
			{
				pathToDisplay: 'automotive',
				title: {key: 'Automotive OS'},
				description: {key: 'devicespage_text_automotiveOS_web'},
				image: {
					src: 'a616956cedc3b48cd5763639e97065e4.png',
					aspectRatio: '720/420',
					alt: 'Automotive',
				},
			},
			{
				pathToDisplay: 'androidauto',
				image: {
					src: 'a698dbb6cdfa6e52b93c564bd4d70568.png',
					aspectRatio: '720/420',
					alt: 'Android Auto',
				},
				title: {key: 'Android Auto'},
				description: {key: 'devicespage_text_androidauto_web'},
			},
			{
				pathToDisplay: 'carplay',
				image: {
					src: '95e81a76ea957e58f5673862b9369697.png',
					aspectRatio: '720/420',
					alt: 'Carplay',
				},
				title: {key: 'Carplay'},
				description: {key: 'devicespage_text_carplay_web'},
			},
			{
				pathToDisplay: 'mirrorlinkauto',
				title: {key: 'MirrorLink via RockScout'},
				description: {
					key: "Plus sûr, plus simple. Avec Deezer pour MirrorLink, accédez à Flow ainsi qu'à toute votre musique directement depuis votre tableau de bord. Disponible dans une sélection de voitures et d'autoradios, pour les mobiles Android compatibles, via l'appli RockScout.",
				},
				image: {
					src: '9409f8c0a72ce7a8c3472515593ffce4.png',
					aspectRatio: '720/420',
					alt: 'Mirror Link',
				},
			},
			{
				pathToDisplay: 'uconnect',
				title: {key: 'Uconnect LIVE'},
				subTitle: {key: 'devicespage_subtitle_uconnectlive_web'},
				description: {
					key: 'devicespage_text_uconnectlive_web',
				},
				image: {
					src: '710595efa6efb82f634bfae1e14490d6.png',
					aspectRatio: '720/420',
					alt: 'Uconnect',
				},
			},
			{
				pathToDisplay: 'waze',
				title: {key: 'Waze'},
				description: {
					key: [
						'wazelandingpage_title_yourideyourvibe_web',
						'devicespage_text_playerintegratedinwazeapp_web',
						'wazelandingpage_text_easyaccessswithbetweenthetwoapps_web',
						'wazelandingpage_text_availablenowonsystemsXandY_web',
					],
					sprintf: ['iOS', 'Android'],
				},
				image: {
					src: '99c0ea8363d962521f6b392827cefa6b.png',
					aspectRatio: '720/420',
					alt: 'Mirror Link',
				},
				learnMoreLink: '/explore/features/waze',
			},
		],
	},
	soundsystems: {
		seoTitle: 'devicessoundsystemspage_text_seotitle_web',
		seoDescription: 'devicessoundsystemspage_text_seodescription_web',
		configs: [
			{
				pathToDisplay: 'homepod',
				title: {key: 'Apple Homepod'},
				description: {key: 'devicespage_text_applehomepod_web'},
				image: {
					src: 'ecb7567666a11164484fbcb224ad9678.png',
					aspectRatio: '632/395',
					alt: 'Apple Homepod',
				},
			},
			{
				pathToDisplay: 'amazonecho',
				title: {key: 'Amazon Echo'},
				description: {key: 'devicespage_text_amazonechov2_web'},
				image: {
					src: '93be697a3597cd4a9726db1948a0e43e.png',
					aspectRatio: '673/371',
					alt: 'Amazon Echo',
				},
				brandImage: {
					src: '423ad4f707dfabf560d1e2a0067444b1.png',
					aspectRatio: '179/42',
					alt: 'Amazon Echo',
				},
				learnMoreLink: '/explore/devices/amazonalexa',
			},
			{
				pathToDisplay: 'bang',
				title: {key: 'Bang & Olufsen'},
				description: {
					key: [
						'Avec Bang & Olufsen, votre expérience musicale va prendre de la hauteur. Accédez à votre compte Deezer, écoutez vos playlists, découvrez de nouveaux titres et trouvez vos futurs morceaux préférés, en qualité audio FLAC.',
						'Que vous vous prépariez pour une soirée, que vous soyez épuisé par votre semaine ou que vous viviez une émotion particulière, Bang & Olufsen met de la magie dans votre musique.',
					],
				},
				image: {
					src: '0e86d2eb8e7388c24741ab05d4d4b9bb.png',
					aspectRatio: '598/417',
					alt: 'BeoSound Moment',
				},
				brandImage: {
					src: 'a6f7b703faaf359730fcdaabca6f3f1e.png',
					aspectRatio: '246/24',
					alt: 'Bang & Olufsen',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'bluesound',
				title: {key: 'Bluesound Network Player'},
				description: {
					key: 'Entre design novateur et son Haute Définition, Bluesound voit grand pour votre intérieur. Contrôlez intuitivement votre musique depuis votre mobile ou tablette et animez toutes les pièces de la maison.',
				},
				image: {
					src: '47f8e04fc488126ede450b00d50a23da.png',
					aspectRatio: '701/356',
					alt: 'Lecteur réseau Bluesound',
				},
				brandImage: {
					src: '4373fce7bbc749680415439663510323.png',
					aspectRatio: '210/36',
					alt: 'Bluesound',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'bose',
				title: {key: 'Bose Music'},
				description: {
					key: 'devicespage_text_bosesoundtouch_web',
					sprintf: [config.get('total_tracks')],
					count: +config.get('total_tracks'),
				},
				image: {
					src: '4b993022755ef54da2293ac59638364d.png',
					aspectRatio: '705/354',
					alt: 'SoundTouch™',
				},
			},
			{
				pathToDisplay: 'control4',
				title: {key: 'Control4'},
				withHifi: true,
				description: {key: 'devicespage_text_control4v2_web'},
				image: {
					src: '87a65c7ec791df3cf26b0d71f11f993d.png',
					aspectRatio: '715/349',
					alt: 'Control4',
				},
				brandImage: {
					src: 'ae739f92d0c443a0b11d8f02f4f5031d.png',
					aspectRatio: '221/46',
					alt: 'Control4',
				},
			},
			{
				pathToDisplay: 'heos',
				title: {key: 'Denon HEOS'},
				description: {
					key: 'Combinant un design sophistiqué et une puissante technologie audio, les enceintes HEOS sans fil Denon emplissent votre maison de musique, le tout avec classe.',
				},
				image: {
					src: '5dfe6b775f5cb2ecdabcc0740c11b4b2.png',
					aspectRatio: '674/370',
					alt: 'Denon HEOS',
				},
				brandImage: {
					src: '234743cf9c374c683e7936e537b4c41e.png',
					aspectRatio: '142/44',
					alt: 'Denon HEOS',
				},
			},
			{
				pathToDisplay: 'devialet',
				title: {key: 'Devialet Phantom'},
				description: {
					key: "Découvrez le son ultra-dense à impact physique de Phantom, l'Implosive Sound Center inventé par Devialet. Connecté en WiFi, écoutez Deezer et redécouvrez votre musique.",
				},
				image: {
					src: 'ba352834cb08ab45daf2abb19c9eca90.png',
					aspectRatio: '713/350',
					alt: 'Devialet',
				},
				brandImage: {
					src: '81de28c4f050360d5c7380b464c74700.png',
					aspectRatio: '182/28',
					alt: 'Devialet',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'googlehome',
				title: {key: 'devicespage_title_googlehomegooglenest_web'},
				description: {key: 'devicespage_text_googlehomegooglenest_web'},
				image: {
					src: '0fcb9034a5572e08b8dbaa28a883b7fe.png',
					aspectRatio: '733/340',
					alt: 'Google Home',
				},
				brandImage: {
					src: '28064b60997b5bc3142e68881803b5e3.png',
					aspectRatio: '216/42',
					alt: 'Google Home',
				},
				withHifi: true,
				learnMoreLink: '/explore/devices/googlehome',
			},
			{
				pathToDisplay: 'harman',
				title: {key: 'Harman Kardon'},
				description: {
					key: 'Un son pur, un design élégant, la musique sans artifice. Que vous soyez dans votre salon ou dans votre chambre, un simple toucher suffit à changer la musique dans toute la maison. Avec le One Touch Follow Me Audio, vous contrôlez à distance la musique qui est diffusée par vos enceintes Harman Kardon. Placez-les où vous le souhaitez, les enceintes Omni sont sans fil pour profiter de Deezer facilement.',
				},
				image: {
					src: '5f9767eb0408047bd705c6dd0e4347ae.png',
					aspectRatio: '630/396',
					alt: 'Harman Kardon',
				},
				brandImage: {
					src: '83061c1eb3fa788d15710a7f34679584.png',
					aspectRatio: '220/34',
					alt: 'Harman Kardon',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'lgaudio',
				title: {key: 'LG Music Flow'},
				description: {
					key: 'Rentrez chez vous, posez votre smartphone, la musique ne s’arrête pas. Le système audio multiroom LG Music Flow détecte votre présence et diffuse la musique que vous êtes en train d’écouter sur Deezer dans toute votre maison. Jouez le même titre partout ou changez de musique selon les pièces, avec LG Music Flow vous gardez le contrôle.',
				},
				image: {
					src: '773fdbeeb1675f51b094b19e9941a685.png',
					aspectRatio: '755/330',
					alt: 'LG',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'simaudio',
				title: {key: 'MOON by Simaudio'},
				description: {
					key: "Avec MOON by Simaudio, découvrez un système audio performant qui vous permettra d'écouter et de profiter de votre musique avec un son de la plus haute qualité. Accédez à Deezer directement sur l'application conçue par Simaudio et retrouvez les fonctionnalités que vous aimez : Flow, votre bibliothèque musicale et des recommandations personnalisées. Donnez à votre musique le son qu’elle mérite avec les systèmes audio MOON.",
				},
				image: {
					src: '773fdbeeb1675f51b094b19e9941a685.png',
					aspectRatio: '755/330',
					alt: 'Simaudio',
				},
				brandImage: {
					src: '64d8762c52e38cc00c19889fcfefd126.png',
					aspectRatio: '420/54',
					alt: 'Simaudio Brand',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'onkyo',
				title: {key: 'Onkyo Network A/V Receivers'},
				description: {
					key: "Préparez-vous à être étonné par votre musique avec les derniers modèles Onkyo. Parcourez tout Deezer directement depuis l'interface Onkyo A/V et profitez d'un son Haute Qualité.",
				},
				withHifi: true,
				image: {
					src: 'e470d018370a441b491eb720a9ad1164.png',
					aspectRatio: '949/263',
					alt: 'Onkyo',
				},
				brandImage: {
					src: 'a0c8015888bdd5cb8ae0ab00f975304a.png',
					aspectRatio: '196/42',
					alt: 'Onkyo Brand',
				},
			},
			{
				displayCountry: ['JP'],
				pathToDisplay: 'onkyorubato',
				title: {key: 'devicespage_title_onkyorubato_web'},
				description: {key: 'devicespage_text_onkyorubato_web'},
				image: {
					src: 'd9fd7ccf41b3a339836ac1e9e92d0eac.png',
					aspectRatio: '770/324',
					alt: 'Onkyo',
				},
				brandImage: {
					src: 'a0c8015888bdd5cb8ae0ab00f975304a.png',
					aspectRatio: '196/42',
					alt: 'Onkyo Brand',
				},
				withHifi: true,
				learnMoreLink: 'http://www.jp.onkyo.com/audiovisual/headphone/',
			},
			{
				displayCountry: ['JP'],
				pathToDisplay: 'pioneer',
				title: {key: 'devicespage_title_pioneerprivate_web'},
				description: {key: 'devicespage_text_pioneerprivate_web'},
				image: {
					src: 'aa15f7348df79738bd8fe28c434bfb44.png',
					aspectRatio: '770/324',
					alt: 'Pioneer',
				},
				brandImage: {
					src: '2afea42766b426b72055157a280be71f.png',
					aspectRatio: '216/42',
					alt: 'Pioneer Brand',
				},
				withHifi: true,
				learnMoreLink: 'http://pioneer-headphones.com/japanese/',
			},
			{
				pathToDisplay: 'samsung',
				title: {key: 'Samsung Wireless Audio 360'},
				description: {
					key: "Conçue pour être acoustiquement parfaite, la gamme Samsung Wireless 360 révolutionne la façon dont nous écoutons de la musique. Préparez-vous à en profiter grâce au travail des ingénieurs du laboratoire audio dernier cri de Samsung en Californie, qui vous proposent ce qui se fait de mieux en la matière. Choisissez l'emplacement de votre haut-parleur, asseyez-vous où vous voulez et faites l'expérience de la même qualité sonore où que vous soyez.",
				},
				image: {
					src: 'a0bf12bdbc8570d0794677803edcde21.png',
					aspectRatio: '612/408',
					alt: 'Samsung',
				},
				withHifi: true,
			},
			{
				title: {key: 'Savant'},
				description: {key: 'devicespage_text_savant_web'},
				image: {
					src: '377492d9dd38360040252ad46174c1b8.png',
					aspectRatio: '665/375',
					alt: 'Savant Smart Audio',
				},
				brandImage: {
					src: 'f33d7eb45dad86a4abda7a7b5efd976e.png',
					aspectRatio: '189/50',
					alt: 'Savant',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'sonos',
				title: {key: 'Sonos'},
				description: {key: 'devicespage_text_sonosv2_web'},
				withHifi: true,
				image: {
					src: 'e3f01e565acf1e73f5a3cf6a459ff8a5.png',
					aspectRatio: '725/344',
					alt: 'Sonos',
				},
				brandImage: {
					src: 'd0a564a69170f5b69ead86e686779aa3.png',
					aspectRatio: '132/30',
					alt: 'Sonos',
				},
			},
			{
				pathToDisplay: 'sonyaudio',
				title: {key: 'Sony High Definition Audio Range'},
				withHifi: true,
				description: {
					key: "Recréez l'acoustique d'un studio d'enregistrement ou d'une salle de concert avec les nouvelles solutions audio de Sony. Alliant technologie novatrice et esthétique minimaliste, ces enceintes et home cinemas connectés offriront une toute nouvelle expérience musicale à votre intérieur.",
				},
				image: {
					src: '1082a7a30cbc456c1c3e8f1eb6100db0.png',
					aspectRatio: '674/370',
					alt: 'Sony',
				},
				brandImage: {
					src: 'ee96b799f6943cc9faf4fddf54296ace.png',
					aspectRatio: '142/28',
					alt: 'Sony',
				},
			},
			{
				pathToDisplay: 'ultimate',
				title: {key: 'Ultimate Ears'},
				description: {key: 'devicespage_text_ultimateears_web'},
				image: {
					src: 'e5d79f6a7d34eb44cdf4d48e69dc5acd.png',
					aspectRatio: '719/347',
					alt: 'Ultimate Ears',
				},
			},
			{
				displayCountry: ['DE', 'AT', 'CH'],
				pathToDisplay: 'teufel',
				title: {key: 'Teufel'},
				description: {key: 'devicespage_text_teufelaudio_web'},
				image: {
					src: 'c9d58a4eaba518ef03e1b02ede1ddb58.png',
					aspectRatio: '608/410',
					alt: 'Teufel Audio',
				},
				brandImage: {
					src: '9ac6462b532ebfb795b442b7c4449347.png',
					aspectRatio: '122/45',
					alt: 'Teufel Audio',
				},
				learnMoreLink: 'teufel',
			},
			{
				pathToDisplay: 'yamaha',
				title: {key: 'Yamaha MusicCast'},
				withHifi: true,
				description: {
					key: "Les produits de la gamme Yamaha MusicCast vous permettent d'écouter vos musiques en streaming en qualité HiFi. Une seule et unique application contrôle l'ensemble des appareils de votre maison et vous permet de diffuser les sons en haute-fidélité d'une pièce à l'autre. Connectez vos appareils Yamaha MusicCast à Deezer HiFi pour une configuration optimum.",
				},
				image: {
					src: 'aa3d894a120acbaad013bd300860320b.png',
					aspectRatio: '688/363',
					alt: 'Yamaha',
				},
				brandImage: {
					src: 'deb75107406c5a3efc20f2f8acb5b752.png',
					aspectRatio: '96/21',
					alt: 'Yamaha',
				},
			},
		],
	},
	tv: {
		seoTitle: 'devicestvgamingpage_text_seotitle_web',
		seoDescription: 'devicestvgamingpage_text_seodescription_web',
		configs: [
			{
				pathToDisplay: 'appletv',
				title: {key: 'Apple TV'},
				description: {
					key: 'devicespage_text_appletv_web',
				},
				image: {
					src: '00bd7e20437016b6e5851528382c7f8b.png',
					aspectRatio: '720/420',
					alt: 'Apple TV',
				},
			},
			{
				pathToDisplay: 'hisense',
				title: {key: 'Vidaa Smart OS'},
				description: {
					key: 'devicespage_text_vidaa_web',
				},
				image: {
					src: '560a878d614f366e63a1c2b9ce12143b.png',
					aspectRatio: '720/420',
					alt: 'Hisense TV',
				},
			},
			{
				pathToDisplay: 'androidtv',
				title: {key: 'Android TV'},
				description: {
					key: "Avec Deezer pour Android TV, transformez votre salon en piste de danse. Tout ce que vous avez à faire, c'est télécharger l'application.",
				},
				image: {
					src: 'c3956771a72d15c74bf026f1aeb6c446.png',
					aspectRatio: '720/420',
					alt: 'Android TV',
				},
			},
			{
				pathToDisplay: 'chromecast',
				title: {key: 'Chromecast'},
				description: {
					key: 'devicespage_text_chromecast_web',
				},
				subDescription: {key: 'devicespage_text_chromecast2_web'},
				image: {
					src: '89aff12e0b355a4116b6a8f6880db216.png',
					aspectRatio: '720/420',
					alt: 'Chromecast',
				},
				withHifi: true,
			},
			{
				pathToDisplay: 'lg',
				title: {key: 'LG TV'},
				description: {
					key: 'devicespage_text_lgtv_web',
				},
				image: {
					src: 'ab6cefd559ce0d15b4c84dffb8a68a42.png',
					aspectRatio: '720/420',
					alt: 'Hisense TV',
				},
			},
			{
				displayCountry: ['FR'],
				pathToDisplay: 'orange',
				title: {key: 'Orange TV'},
				description: {
					key: 'devicespage_text_orangetv_web',
					sprintf: [config.get('total_tracks')],
					count: +config.get('total_tracks'),
				},
				image: {
					src: 'cfec49e82638b36e26f18c80f8ed05b5.png',
					aspectRatio: '720/420',
					alt: 'Orange TV',
				},
			},
			{
				pathToDisplay: 'panasonic',
				title: {key: 'Panasonic TV'},
				description: {
					key: "Admirez de sublimes pochettes d'albums tout en écoutant votre musique préférée sur votre TV Panasonic. Quand la musique est bonne... et belle !",
				},
				image: {
					src: '35ee1472cb409a55ecfc1fb3371f653e.png',
					aspectRatio: '720/420',
					alt: 'Panasonic TV',
				},
			},
			{
				pathToDisplay: 'samsungtv',
				title: {key: 'Samsung TV'},
				description: {
					key: 'devicespage_text_samsungtv_web',
				},
				image: {
					src: '55729cf0a4463045eec5f1b795dbacc4.png',
					aspectRatio: '720/420',
					alt: 'Samsung TV',
				},
			},
			{
				pathToDisplay: 'sony',
				title: {key: 'Sony Bravia'},
				description: {
					key: 'devicespage_text_sonybravia_web',
					sprintf: [config.get('total_tracks')],
					count: +config.get('total_tracks'),
				},
				image: {
					src: '4eb1c462c78e25b22abdb4fd14fcd815.png',
					aspectRatio: '720/420',
					alt: 'Sony TV',
				},
			},
		],
	},
	voice: {
		seoTitle: 'devicesvoiceassistantspage_text_seotitle_web',
		seoDescription: 'devicestvgamingpage_text_seodescription_web',
		configs: [
			{
				pathToDisplay: 'amazonalexa',
				title: {key: 'Amazon Alexa'},
				description: {
					key: 'devicespage_text_amazonalexa_web',
				},
				image: {
					src: '58d2bfe4c5487e941d9a584bc2b01aa7.png',
					aspectRatio: '720/420',
					alt: 'Amazon Alexa',
				},
			},
			{
				pathToDisplay: 'siri',
				title: {key: 'devicespage_title_applesiri_web'},
				description: {
					key: [
						'applesiri_title_sayitplayit_web',
						'devicespage_text_applesiriv2_web',
						'devicespage_text_applesiriavailableios13_web',
						'devicespage_text_availableondevice_web',
					],
					sprintf: [
						`<a href='/explore/devices/soundsystems' target="_blank" class="link-primary">Apple Homepod</a>`,
					],
					hasLink: true,
				},
				image: {
					src: 'e7cee074014cffe09760cf7688646534.png',
					aspectRatio: '720/420',
					alt: 'Siri',
				},
				withIosBadge: true,
			},
			{
				pathToDisplay: 'google',
				title: {key: 'devicespage_title_googleassistant_web'},
				description: {key: 'devicespage_text_googleassistant_web'},
				image: {
					src: '97c42fe88e9ee12ba9fc44752b5b8966.png',
					aspectRatio: '720/420',
					alt: 'Google Home',
				},
				learnMoreLink: '/explore/devices/googlehome',
			},
		],
	},
	wearables: {
		seoTitle: 'deviceswearablespage_text_seotitle_web',
		seoDescription: 'deviceswearablespage_text_seodescription_web',
		configs: [
			{
				pathToDisplay: 'applewatch',
				title: {key: 'Apple Watch'},
				description: {key: 'devicespage_text_applewatch3_web'},
				image: {
					src: 'b22d3fa912ebb9def918c3a2c67f43e7.png',
					aspectRatio: '720/420',
					alt: 'Apple Watch',
				},
				subDescription: {
					key: 'devicespage_text_foriosversionXorhigher_web',
					sprintf: [18.5],
				},
				withIosBadge: true,
			},
			{
				pathToDisplay: 'garmin',
				image: {
					src: '1be427e156a72efe76c9210ed01594a9.png',
					aspectRatio: '720/420',
					alt: 'Garmin',
				},
				title: {key: 'Garmin'},
				description: {key: 'devicespage_text_garmin_web'},
				brandImage: {
					src: '818dce2ccc57c721dbe57f4e0856090a.png',
					aspectRatio: '73/21',
					alt: 'Garmin',
				},
			},
			{
				pathToDisplay: 'androidwear',
				title: {key: 'Wear OS by Google'},
				description: {
					key: 'Contrôlez votre app mobile Deezer depuis votre poignet. Choisissez votre musique, ajustez le volume et plus encore depuis votre montre.',
				},
				image: {
					src: '2454be1d0640671d209162ac7d50d586.png',
					aspectRatio: '720/420',
					alt: 'Wear OS by Google',
				},
				subDescription: {
					key: 'devicespage_text_forandroidversionXorhigher_web',
					sprintf: [6],
				},
				withAndroidBadge: true,
			},
		],
	},
};
